/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The value line's *where to get it* zone** — `X23` / `X24`, `XS3` slice 2 (#1222).
 *
 * One field. An operator types a literal, a context path or a `{macro}` into it and the row
 * **reports what each one is** without them choosing anything: a small mono read-out at the head of
 * the field says `literal`, `path` or `macro → path`. There is no mode and no value-type choice.
 *
 * **The control owns the text**, which is the rule this codebase has paid for twice — once on
 * `NumberField` (`Number('')` is `0`, so clearing the box wrote a zero back and the field could not
 * be emptied) and once on `ListField` (parsing every keystroke deleted the comma that created the
 * second list member). The general form: *a control whose text and whose value are different shapes
 * must own the text.* Here the shapes differ three ways — a literal is a `staticValue`, a path is a
 * `CalculatedExpression`, a macro is a `macroToken` — so round-tripping the operator's keystrokes
 * through the parsed leaf would reclassify the value mid-word. Typing `session.` would be a literal
 * for six characters and then become a path, rewriting the leaf underneath the cursor each time.
 *
 * So: local draft text, derived on change, and **re-synced from the parent only when the parent's
 * value disagrees with what the draft already means**. `session.user.ip` typed one character at a
 * time never reseats itself.
 */

import React from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { MappingAutocompleteInput, type MappingSuggestion } from '../inputs/MappingAutocompleteInput';
import { getValueSuggestions } from '../inputs/data-model-schema';
import type { MappingLeaf, ScalarType } from './ortb-body-editor.adapters';
import { coerceStaticValue, isScalarArray, parseScalarArray } from './ortb-static-value';
import { coercibleScalar } from '@expression-yield';
import { deriveValueKind, rendersOnValueLine, type DerivedValue } from './value-line';
import { expressionDefaultOf, macroTokenName } from './mapping-value-round-trip';
import { summarizeMacroExpression } from './DecoratorExpressionEditor';
import { withPath } from './MappingControl';
import { useExpressionLibrary } from '../../../hooks/useExpressionLibrary';
import { useArtifactUsage } from '../../../hooks/useArtifactUsage';
import { entryVersion, referenceIsBehind } from './shared-expression-version';
import { useRecordRevisit } from './useRecordRevisit';
import type { ReferenceExpression } from '../../../types/decorator-expression';
import { useTestedScope } from './tested-scope-context';
import { outOfScopeReason } from './tested-value.model';

/**
 * **What a typed literal is stored as** — a single-value type, or `'array'` where the destination
 * takes a list (`4f`, `#1419`). A list is typed on the line as comma-separated text and stored as a
 * list, split the way the list editor splits (`parseScalarArray`).
 */
export type LiteralKind = ScalarType | 'array';

/** Whether this row's literal is a list: its own Type says so, or it states none and the field takes one. */
function literalIsList(leaf: MappingLeaf, literalType: LiteralKind | null): boolean {
  return leaf.asType === 'array' || (leaf.asType === undefined && literalType === 'array');
}

/**
 * **A flat list shows on the line where the row's literal is a list** (`4f`, `#1419`). Without it a
 * field that takes a list was seeded `[]`, which rendered as a read-only *JSON* summary, so there was
 * nowhere to type the path the operator wanted to map. A list on any other row keeps the editor it has
 * always had: showing it here would make one keystroke turn it into text.
 */
export function showsListOnLine(leaf: MappingLeaf, literalType: LiteralKind | null): boolean {
  return leaf.kind === 'static' && isScalarArray(leaf.staticValue) && literalIsList(leaf, literalType);
}

/**
 * The text a leaf currently shows in the one field.
 *
 * **A macro is stored bare and shown braced**, which is the model's own convention (`textToToken`
 * strips the delimiters on the way in). The value line has to speak it in both directions or the
 * store normalises the token, the field re-syncs to `app_name`, and the read-out then reports a
 * perfectly good macro as a `literal` — which is exactly what the slice 2 rehearsal caught.
 */
export function leafToText(leaf: MappingLeaf, literalType: LiteralKind | null = null): string {
  if (showsListOnLine(leaf, literalType)) return (leaf.staticValue as unknown[]).join(', ');
  if (leaf.kind === 'expression' && leaf.expression?.type === 'macro') {
    const token = leaf.expression.name.trim();
    return token ? `{${token.replace(/[{}[\]]/g, '')}}` : '';
  }
  if (leaf.kind === 'expression') {
    // A `static` expression is a literal the leaf happens to spell as an expression — what clearing
    // an errand on a literal produces. It reads back as its own text, or the line would render an
    // empty box over an authored value (`X38`).
    if (leaf.expression?.type === 'static') return leaf.expression.value;
    return leaf.expression?.type === 'calculated' ? leaf.expression.path : '';
  }
  const v = leaf.staticValue;
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  // A structured static has no single-field representation and is routed to the Static tab's JSON
  // editor instead. Returning `[object Object]` here would be a value the operator could then
  // *save over the top of*, which is worse than showing nothing.
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

/**
 * Write what was typed, as the kind it reads as.
 *
 * **The expression's parts survive a path→path edit and only that.** A regex belongs to a derived
 * value; carrying it onto a literal would show a chip describing something the model cannot apply,
 * which is the *reads as proof* defect. Losing it when the operator deliberately replaces a path
 * with a literal is a real edit, not a silent view change — the distinction `X26` draws.
 */
export function textToLeaf(
  leaf: MappingLeaf,
  text: string,
  acceptsMacro = true,
  /**
   * **The scalar type the destination declares, which a typed literal is stored as** (`#923`,
   * regressed by this line from `XS3` slice 2 to 2026-09-18). `bidfloor` typed as `5` was stored as
   * the text `"5"` and sent quoted, which is the type violation `#923` closed on the old static
   * editor. The row's own **Type** wins where it states one. `null` where nothing is declared.
   */
  literalType: LiteralKind | null = null,
  /** `false` where the destination takes only a fixed value: the text is the literal (`#1580`). */
  acceptsPath = true,
): MappingLeaf {
  const derived = deriveValueKind(text, { acceptsMacro, acceptsPath });
  const base = { path: leaf.path, asType: leaf.asType };
  // A macro is a `macro` node (`X58`), bare on the way in. Its note and default survive the edit, as a
  // path's do through `withPath`.
  if (derived.kind === 'macro') {
    const prev = leaf.expression;
    const carriedDefault = prev ? expressionDefaultOf(prev, null) : undefined;
    return {
      ...base,
      kind: 'expression',
      expression: {
        type: 'macro',
        name: text.trim().replace(/[{}[\]]/g, ''),
        ...(prev?.description ? { description: prev.description } : {}),
        ...(carriedDefault !== undefined ? { default: carriedDefault } : {}),
      },
    };
  }
  if (derived.kind === 'path')
    return { ...base, kind: 'expression', expression: withPath(leaf.expression, text.trim()) };
  /**
   * **A note survives a literal edit** (`X38`), which is `reshapeExpression`'s rule — *a note is
   * about the mapping, not about the shape it currently has* — applied at the one boundary that
   * could still drop it. A `static` leaf has nowhere to keep a description (`NoteControl` says so),
   * so a value that already carries one stays spelled as an expression rather than losing it on the
   * keystroke that made it a literal.
   */
  const note = leaf.kind === 'expression' ? leaf.expression?.description : undefined;
  if (note) return { ...base, kind: 'expression', expression: { type: 'static', value: text, description: note } };
  // A list is typed as comma-separated text and stored as a list — empty is `[]`, which stays
  // well-formed JSON on the wire.
  if (literalIsList(leaf, literalType)) return { ...base, kind: 'static', staticValue: parseScalarArray(text) };
  // A structured `Type` is a declaration about the value, not a parse to perform on a literal.
  const asType = coercibleScalar(leaf.asType) ?? (literalType === 'array' ? null : literalType);
  // The draft stays the operator's text (the field owns it); only the stored value is coerced, and
  // `coerceStaticValue` keeps mid-typing states like `2.` or `-` as text rather than destroying them.
  return { ...base, kind: 'static', staticValue: asType ? coerceStaticValue(text, asType) : text };
}

interface ValueLineProps {
  leaf: MappingLeaf;
  onChange: (next: MappingLeaf) => void;
  /** Suggestions for the read side. `null` means *use the outbound schema*, which is a real answer. */
  pathSuggestions: ((query: string) => MappingSuggestion[] | Promise<MappingSuggestion[]>) | null;
  placeholder: string | null;
  /**
   * Whether a `{macro}` is a value this surface can read from — see `ValueKindOptions`. `false` on
   * an inbound surface, where it classifies as the literal it will be stored as.
   */
  acceptsMacro: boolean;
  /**
   * What to say when a `{macro}` is typed on a surface that does not accept one — the surface's own
   * sentence, so the refusal and the warning cannot come to describe one rule two ways. `null` where
   * the surface accepts macros and the question does not arise.
   */
  macroRefusal: string | null;
  /**
   * **Why this destination takes only a fixed value**, or `null` where it takes a worked-out one too
   * (`acceptsDerived`, `#1580`). Set, what is typed is stored as the literal, nothing is suggested,
   * and text that reads as a path or a macro is answered with this sentence rather than stored quietly.
   * Required and nullable (`#1134`).
   */
  derivedRefusal: string | null;
  /** Rendered to the right of the field — the Default control, which shares the line. */
  trailing: React.ReactNode;
  /**
   * The scalar type the destination declares, so a typed literal is stored as it (`#923`) — see
   * {@link textToLeaf}. Required and nullable (`#1134`): the surface that forgot is the regression.
   */
  literalType: LiteralKind | null;
}

export const ValueLine: React.FC<ValueLineProps> = ({
  leaf,
  onChange,
  pathSuggestions,
  placeholder,
  acceptsMacro,
  macroRefusal,
  derivedRefusal,
  trailing,
  literalType,
}) => {
  const acceptsPath = derivedRefusal === null;
  const fromLeaf = leafToText(leaf, literalType);
  const [draft, setDraft] = React.useState(fromLeaf);
  // Names the case in an equation's summary where an operand reads a tested value (`X55b`).
  const testedScope = useTestedScope();

  // Re-sync only on a **real** disagreement: the parent holds a value this draft does not already
  // mean. Comparing the parent's text against the draft's *derived* leaf rather than against the
  // draft string is what lets `user.deviceType` stay typed while the model holds its canonical form.
  React.useEffect(() => {
    if (leafToText(textToLeaf(leaf, draft, acceptsMacro, literalType, acceptsPath), literalType) !== fromLeaf)
      setDraft(fromLeaf);
  }, [fromLeaf]);

  /**
   * **A shape the one field cannot hold reads back as a summary, and is edited below** (`X23`).
   *
   * A conditional or a computed expression has no text form, so rendering the editable field for it
   * would show an **empty box beside a fully authored value** — and one keystroke in that box would
   * replace the whole expression with a literal. The summary is the same
   * `summarizeMacroExpression` the collapsed row uses, so the two cannot describe one value two
   * ways.
   */
  if (leaf.kind === 'expression' && leaf.expression?.type === 'reference') {
    return (
      <SharedReadOut
        reference={leaf.expression}
        onChange={(next) => onChange({ ...leaf, expression: next })}
        trailing={trailing}
      />
    );
  }

  if (leaf.kind === 'expression' && leaf.expression?.type === 'bound') {
    return <TestedReadOut name={leaf.expression.name} trailing={trailing} />;
  }

  if (!rendersOnValueLine(leaf) && !showsListOnLine(leaf, literalType)) {
    const summary =
      leaf.kind === 'expression' && leaf.expression
        ? summarizeMacroExpression(
            leaf.expression,
            undefined,
            (name) => testedScope.find((e) => e.name === name)?.label,
          )
        : 'JSON';
    return (
      <HStack gap={2} align="center">
        <Box flex="1" minWidth={0}>
          <HStack gap={0} align="stretch">
            <ReadOut kind={leaf.kind === 'expression' ? 'expression' : 'literal'} label={LABEL_FOR_SHAPE(leaf)} />
            <Box
              flex="1"
              minWidth={0}
              display="flex"
              alignItems="center"
              px={2.5}
              py={1}
              borderWidth="1px"
              borderColor="border.subtle"
              borderRightRadius="sm"
              bg="bg.subtle"
              fontFamily="mono"
              fontSize="xs"
              color="fg.default"
            >
              <Text truncate>{summary}</Text>
            </Box>
          </HStack>
        </Box>
        {trailing}
      </HStack>
    );
  }

  const derived: DerivedValue = deriveValueKind(draft, { acceptsMacro, acceptsPath });
  // Typed as a path or a macro on a destination that takes only a fixed value: kept as the text it is,
  // and answered (pattern 42), never stored quietly as if it were what was meant.
  const derivedOnAFixedSurface =
    derivedRefusal !== null && ['path', 'macro'].includes(deriveValueKind(draft.trim()).kind);
  /**
   * **A brace on a surface that has no macros is a warning, not a silent literal** (`XS3` slice 3).
   *
   * With the Macro tab gone the only thing left of the inbound surface's refusal is what happens
   * when somebody types one anyway. Storing it quietly as a literal is honest about the model and
   * silent about the mistake, and this is the moment the operator is most likely to believe the row.
   */
  const macroOnAMacrolessSurface = !acceptsMacro && !!macroTokenName(draft.trim());

  return (
    <HStack gap={2} align="center">
      <Box flex="1" minWidth={0}>
        <HStack gap={0} align="stretch">
          {/* The read-out. Derived from what was typed, never chosen — `X24`. */}
          <ReadOut
            kind={derived.kind}
            label={derived.label}
            title={
              derived.reaches
                ? `Resolves through ${derived.reaches}`
                : derived.canonical
                  ? `Stored as ${derived.canonical}`
                  : undefined
            }
          />
          <Box flex="1" minWidth={0} css={{ '& input': { borderLeftRadius: 0 } }}>
            <MappingAutocompleteInput
              size="sm"
              bg="bg.card"
              value={draft}
              /**
               * **Open on focus, because the empty box is now the top of the model** (`#1249`).
               *
               * The default of 1 was there to stop an empty box opening a hundred rows, and
               * segment-scoping retired that reason: every source this line can be handed answers
               * an empty query with the roots, or with a destination's own short list — measured
               * 2026-09-11 at 6, 6 and 21. Leaving it at 1 would have meant the operator's first
               * keystroke is a **search** (`s` matches most of the model), which is the complaint
               * that opened `#1249` surviving on every surface but the two that already passed 0.
               */
              minChars={0}
              getSuggestions={acceptsPath ? (pathSuggestions ?? ((q) => getValueSuggestions(q))) : () => []}
              placeholder={
                placeholder ??
                (!acceptsPath ? 'A value' : acceptsMacro ? 'A value, a session path, or {macro}' : 'A value or a path')
              }
              onChange={(next) => {
                setDraft(next);
                onChange(textToLeaf(leaf, next, acceptsMacro, literalType, acceptsPath));
              }}
            />
          </Box>
        </HStack>
        {/* What the read-out could not fit. Shown, not hidden behind the hover, because a
          canonicalised path is a difference between what was typed and what is stored (`#1213`). */}
        {(derived.reaches || derived.canonical) && (
          <Text fontSize="xs" color="fg.muted" fontFamily="mono" mt={1} truncate>
            {derived.reaches ? `→ ${derived.reaches}` : `stored as ${derived.canonical!}`}
          </Text>
        )}
        {macroOnAMacrolessSurface && macroRefusal && !derivedOnAFixedSurface && (
          <Text fontSize="xs" color="warning.ink" mt={1}>
            {macroRefusal}
          </Text>
        )}
        {derivedOnAFixedSurface && (
          <Text fontSize="xs" color="warning.ink" mt={1} data-testid="derived-refusal">
            {derivedRefusal}
          </Text>
        )}
      </Box>
      {trailing}
    </HStack>
  );
};

/**
 * The kind read-out at the head of the field — one rendering, whether the value is editable here or
 * shown as a summary. Two copies would let the two states describe one vocabulary differently, which
 * is the whole reason `X24` made this a read-out instead of a picker.
 */
export const ReadOut: React.FC<{ kind: DerivedValue['kind']; label: string; title?: string }> = ({
  kind,
  label,
  title,
}) => (
  <Box
    display="flex"
    alignItems="center"
    px={2.5}
    borderWidth="1px"
    borderRightWidth={0}
    borderColor="border.subtle"
    borderLeftRadius="sm"
    bg={kind === 'empty' ? 'bg.subtle' : 'info.tint'}
    color={kind === 'empty' ? 'fg.subtle' : 'info.ink'}
    fontFamily="mono"
    fontSize="xs"
    whiteSpace="nowrap"
    title={title}
  >
    {label || '—'}
  </Box>
);

/**
 * **A row that uses a shared expression says which one, and what it does** (`X5`, `X30`, `XS5`).
 *
 * The read-out names the expression rather than its shape, because the shape is the library's
 * business and the operator's question here is *which shared thing is this row using*. Beneath the
 * name it renders the referenced expression's **own** summary, so the row still says what it does
 * without the operator opening the library.
 *
 * **It resolves the name itself**, the way `SharedExpressionPicker` reads its own list: threading a
 * lookup through every surface that can hold a value would put it behind four chances to forget,
 * which is the `#1134` failure mode `MappingControlProps` was made required to close.
 *
 * ## Three outcomes, and it must never collapse them
 *
 * Loading, failed and **gone** are different facts, and the third is the only one that is bad news.
 * Saying *no longer in the library* while the request is still in flight would send an operator to
 * re-author an expression that exists — `SharedExpressionPicker`'s rule, and `#1002`'s direction:
 * the false claim is worse than the omission.
 *
 * A genuinely dead address is stated plainly, and it matches what the runtime does with it: the
 * config read leaves it unresolved and the value is not sent.
 */
const SharedReadOut: React.FC<{
  reference: ReferenceExpression;
  /** Where opening this row records the version it is now showing (`#1302`). */
  onChange: (next: ReferenceExpression) => void;
  trailing?: React.ReactNode;
}> = ({ reference, onChange, trailing }) => {
  const expressionRef = reference.ref;
  const { data, isLoading, isError } = useExpressionLibrary();
  const entry = (data ?? []).find((candidate) => candidate.id === expressionRef);
  const behind = referenceIsBehind(reference, entryVersion(entry));
  // This line renders only in an open row, so rendering it is the revisit.
  useRecordRevisit(reference, entryVersion(entry), onChange);

  const detail = isLoading
    ? 'looking…'
    : isError
      ? 'the library could not be read, so nothing is claimed about this'
      : entry
        ? summarizeMacroExpression(entry.expression)
        : 'no longer in the library — this value will not be sent';

  return (
    <HStack gap={2} align="center">
      <Box flex="1" minWidth={0}>
        <HStack gap={0} align="stretch">
          <ReadOut kind="shared" label={entry ? `from ${entry.name}` : 'shared'} title={expressionRef} />
          <SharedReachMark expressionRef={expressionRef} known={!!entry} />
          {behind && <ChangedMark />}
          <Box
            flex="1"
            minWidth={0}
            display="flex"
            alignItems="center"
            px={2.5}
            py={1}
            borderWidth="1px"
            borderColor="border.subtle"
            borderRightRadius="sm"
            bg="bg.subtle"
            fontFamily="mono"
            fontSize="xs"
            color={!isLoading && !isError && !entry ? 'warning.ink' : 'fg.default'}
          >
            <Text truncate>{detail}</Text>
          </Box>
        </HStack>
      </Box>
      {trailing}
    </HStack>
  );
};

/**
 * **A value an earlier case tested, named by where it was tested** (`X55b`, `S3b`).
 *
 * The stored name is a generated id and means nothing to an operator, so the read-out says *which
 * case* and, beneath it, *what that case tests* — the same two things the chip's list offered. A read
 * whose case is no longer before it says so here, in the place the operator is looking, and the
 * engine resolves it to nothing: an error, never a block.
 */
const TestedReadOut: React.FC<{ name: string; trailing?: React.ReactNode }> = ({ name, trailing }) => {
  const scope = useTestedScope();
  const entry = scope.find((candidate) => candidate.name === name);
  const problem = outOfScopeReason(name, scope);
  return (
    <VStack align="stretch" gap={1}>
      <HStack gap={2} align="center">
        <Box flex="1" minWidth={0}>
          <HStack gap={0} align="stretch">
            <ReadOut kind="expression" label={entry ? `tested in ${entry.label}` : 'tested value'} />
            <Box
              flex="1"
              minWidth={0}
              display="flex"
              alignItems="center"
              px={2.5}
              py={1}
              borderWidth="1px"
              borderColor={problem ? 'error.border' : 'border.subtle'}
              borderRightRadius="sm"
              bg="bg.subtle"
              fontFamily="mono"
              fontSize="xs"
              color="fg.default"
            >
              <Text truncate>{entry?.tests || '—'}</Text>
            </Box>
          </HStack>
        </Box>
        {trailing}
      </HStack>
      {problem && (
        <Text fontSize="xs" color="error.ink" role="alert">
          {problem}
        </Text>
      )}
    </VStack>
  );
};

/**
 * **What else this reaches** (`X30`, `XS5`) — *"a shared expression is a chip that states its reach."*
 *
 * It is deliberately a **reach** statement and not a provenance one (pattern 21): *what breaks if I
 * change this* flows the other way from *where this came from*, and the operator standing on a row
 * about to edit the library is asking the first.
 *
 * `1 use` is this row and nothing else, and it is still worth rendering: the number an operator
 * needs to see is the one that is **more** than one, and a mark that appeared only above a threshold
 * would make its own absence ambiguous — *nobody else uses this* and *nothing has been counted* would
 * look identical.
 *
 * **Silent while it does not know.** Loading, a failed lookup and an address that is not in the
 * library all render nothing rather than `0 uses`, which is `X33a`'s false claim: a count of zero
 * about an expression this very row is using is wrong on its face.
 */
const SharedReachMark: React.FC<{ expressionRef: string; known: boolean }> = ({ expressionRef, known }) => {
  const { data } = useArtifactUsage('expression', expressionRef, known);
  // **Dependents this operator cannot see still count.** They are counted rather than named for
  // exactly this reason — a reach statement that dropped them would tell somebody a change is safe
  // because they lack permission to see what it breaks.
  const count = data ? data.usedBy.length + data.hiddenCount : undefined;
  if (count === undefined || count === 0) return null;
  return (
    <Box
      display="flex"
      alignItems="center"
      px={2}
      borderWidth="1px"
      borderRightWidth={0}
      borderColor="border.subtle"
      bg="info.tint"
      color="info.ink"
      fontSize="xs"
      whiteSpace="nowrap"
    >
      {count === 1 ? '1 use' : `${count} uses`}
    </Box>
  );
};

/** The read-out's word for a value the line cannot hold. Structured statics are JSON; the rest are expressions. */
const LABEL_FOR_SHAPE = (leaf: MappingLeaf): string => (leaf.kind === 'expression' ? 'expression' : 'JSON');

/**
 * **The shared expression moved after this row recorded it** (`#1302`, `X30a`).
 *
 * `X5` made a picked entry an **address**, so a change in the library reaches every row — which is
 * the point, and is also how a row's authored intent goes quiet: the author chose one expression and
 * is now sending another, correctly, with nothing on screen saying the thing moved.
 *
 * **A warning, and not an error, because nothing here is wrong.** The new expression may be exactly
 * what this row wants. It sits at the severity this repo reserves for *this may not be what you
 * meant*, and its words claim only what was observed — *not the version you recorded* — never that
 * the value is bad. A surface that warns about correct behaviour is how an operator learns to ignore
 * the warnings that matter.
 *
 * **Opening it is the revisit** (Todd, 2026-09-10, choosing among three): the panel records the
 * version now in force, so the mark clears by being looked at rather than by an artifact save that
 * had nothing to do with the expression.
 */
const ChangedMark: React.FC = () => (
  <Box
    display="flex"
    alignItems="center"
    px={2}
    borderWidth="1px"
    borderRightWidth={0}
    borderColor="warning.border"
    bg="warning.tint"
    color="warning.ink"
    fontSize="xs"
    whiteSpace="nowrap"
    title="This shared expression has been edited since this row recorded it. Open it to see what it does now."
  >
    changed
  </Box>
);
